import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface Usuario {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface SueldoConfig {
  operadorId: number;
  mes: number;
  anio: number;
  sueldoReferencia: number;
  valorDiaPago: number;
  tipoPago: 'objetivos' | 'horas'; // Nuevo campo
  valorHora?: number; // Para pago por horas
  plusTipo: 'porcentaje' | 'fijo';
  plusPorcentaje: number;
  plusMontoFijo: number;
  objetivoAuditorias?: number;
  objetivoCorrecciones?: number;
  cobraFrancos: boolean;
  esSueldoFijo: boolean;
  adminGanaSueldo: boolean;
  fechaIngreso?: string;
  diasVacaciones: number;
  francosMarcados?: number[]; // Array de días marcados como franco
}

const SueldosAdminPage: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [config, setConfig] = useState<SueldoConfig>({
    operadorId: 0,
    mes: new Date().getMonth() + 1,
    anio: new Date().getFullYear(),
    sueldoReferencia: 25000,
    valorDiaPago: 50,
    tipoPago: 'objetivos',
    valorHora: 0,
    plusTipo: 'porcentaje',
    plusPorcentaje: 0,
    plusMontoFijo: 0,
    cobraFrancos: true,
    esSueldoFijo: false,
    adminGanaSueldo: false,
    diasVacaciones: 0,
    francosMarcados: []
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      const response = await api.get('/users');
      
      // Probar diferentes estructuras
      let empleados = [];
      if (response.data.users) {
        empleados = response.data.users;
      } else if (response.data.data) {
        empleados = response.data.data;
      } else if (Array.isArray(response.data)) {
        empleados = response.data;
      } else {
        empleados = response.data;
      }
      
      const empleadosFiltrados = empleados.filter(u => 
        ['operador', 'supervisor', 'admin', 'contable'].includes(u.role)
      ) || [];
      
      setUsuarios(empleadosFiltrados);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSelect = async (user: Usuario) => {
    setSelectedUser(user);
    setLoading(true);
    
    try {
      // Intentar cargar configuración existente
      const response = await api.get(`/sueldos?operadorId=${user.id}&mes=${config.mes}&anio=${config.anio}`);
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        // Si existe configuración, cargarla
        const existingConfig = response.data.data[0];
        
        setConfig(prev => ({
          ...prev,
          operadorId: user.id,
          sueldoReferencia: parseFloat(existingConfig.sueldoReferencia) || getRoleDefaultSalary(user.role),
          valorDiaPago: parseFloat(existingConfig.valorDiaPago) || Math.round(prev.sueldoReferencia / 30),
          tipoPago: existingConfig.tipoPago || 'objetivos',
          valorHora: parseFloat(existingConfig.valorHora) || Math.round(prev.sueldoReferencia / 160),
          plusTipo: existingConfig.plusTipo || 'porcentaje',
          plusPorcentaje: parseFloat(existingConfig.plusPorcentaje) || 0,
          plusMontoFijo: parseFloat(existingConfig.plusMontoFijo) || 0,
          objetivoAuditorias: existingConfig.objetivoAuditorias || undefined,
          objetivoCorrecciones: existingConfig.objetivoCorrecciones || (user.role === 'supervisor' ? 50 : undefined),
          esSueldoFijo: existingConfig.esSueldoFijo !== undefined ? existingConfig.esSueldoFijo : ['admin', 'contable'].includes(user.role),
          cobraFrancos: existingConfig.cobraFrancos !== undefined ? existingConfig.cobraFrancos : user.role !== 'admin',
          adminGanaSueldo: existingConfig.adminGanaSueldo !== undefined ? existingConfig.adminGanaSueldo : user.role !== 'admin',
          diasVacaciones: existingConfig.diasVacaciones || 0,
          francosMarcados: existingConfig.francosMarcados || []
        }));
        
        setMessage(`✅ Configuración cargada para ${user.name}`);
      } else {
        // Si no existe configuración, usar defaults
        const sueldoRef = getRoleDefaultSalary(user.role);
        setConfig(prev => ({
          ...prev,
          operadorId: user.id,
          esSueldoFijo: ['admin', 'contable'].includes(user.role),
          cobraFrancos: user.role !== 'admin',
          adminGanaSueldo: user.role === 'admin' ? false : true,
          sueldoReferencia: sueldoRef,
          valorDiaPago: Math.round(sueldoRef / 30),
          tipoPago: user.role === 'operador' ? 'objetivos' : 'objetivos',
          valorHora: user.role === 'operador' ? Math.round(sueldoRef / 160) : 0,
          objetivoAuditorias: undefined,
          objetivoCorrecciones: user.role === 'supervisor' ? 50 : undefined,
          plusTipo: 'porcentaje',
          plusPorcentaje: 0,
          plusMontoFijo: 0,
          diasVacaciones: 0,
          francosMarcados: []
        }));
        
        setMessage(`📝 Configuración por defecto para ${user.name}`);
      }
      
      setTimeout(() => setMessage(''), 3000);
      
    } catch (error: any) {
      console.error('Error cargando configuración:', error);
      // Si hay error, usar defaults
      const sueldoRef = getRoleDefaultSalary(user.role);
      setConfig(prev => ({
        ...prev,
        operadorId: user.id,
        esSueldoFijo: ['admin', 'contable'].includes(user.role),
        cobraFrancos: user.role !== 'admin',
        adminGanaSueldo: user.role === 'admin' ? false : true,
        sueldoReferencia: sueldoRef,
        valorDiaPago: Math.round(sueldoRef / 30),
        tipoPago: user.role === 'operador' ? 'objetivos' : 'objetivos',
        valorHora: user.role === 'operador' ? Math.round(sueldoRef / 160) : 0,
        objetivoAuditorias: undefined,
        objetivoCorrecciones: user.role === 'supervisor' ? 50 : undefined,
        plusTipo: 'porcentaje',
        plusPorcentaje: 0,
        plusMontoFijo: 0,
        diasVacaciones: 0
      }));
      
      setMessage(`⚠️ Error cargando config, usando defaults para ${user.name}`);
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDefaultSalary = (role: string) => {
    const defaults = {
      operador: 25000,
      supervisor: 40000,
      admin: 50000,
      contable: 45000
    };
    return defaults[role] || 25000;
  };

  // Función para recargar configuración cuando cambia el período
  const handlePeriodChange = (field: 'mes' | 'anio', value: number) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    
    // Si hay un usuario seleccionado, recargar su configuración para el nuevo período
    if (selectedUser) {
      setTimeout(() => {
        handleUserSelect(selectedUser);
      }, 100);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setLoading(true);
    try {
      // Limpiar y validar todos los números antes de enviar
      const cleanConfig = {
        ...config,
        operadorId: selectedUser.id,
        sueldoReferencia: Number(config.sueldoReferencia) || 25000,
        valorDiaPago: Number(config.valorDiaPago) || Math.round(config.sueldoReferencia / 30),
        valorHora: Number(config.valorHora) || Math.round(config.sueldoReferencia / 160),
        plusPorcentaje: Number(config.plusPorcentaje) || 0,
        plusMontoFijo: Number(config.plusMontoFijo) || 0,
        objetivoCorrecciones: Number(config.objetivoCorrecciones) || null,
        diasVacaciones: Number(config.diasVacaciones) || 0
      };

      
      const response = await api.post('/sueldos/configurar', cleanConfig);
      if (response.data.success) {
        setMessage(`✅ Sueldo configurado para ${selectedUser.name}`);
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error: any) {
      console.error('Error completo:', error);
      setMessage(`❌ Error: ${error.response?.data?.error || 'Error del servidor'}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const calcularMesExamples = () => {
    if (!selectedUser) return null;
    
    const ejemplos = {
      operador: config.tipoPago === 'horas'
        ? `Ejemplo: 160 horas trabajadas × $${config.valorHora || Math.round(config.sueldoReferencia / 160)}/hora = $${config.sueldoReferencia.toLocaleString()}`
        : `Ejemplo: Auditorías $15,000 ganadas ÷ $${config.sueldoReferencia.toLocaleString()} = 60% del objetivo`,
      supervisor: `Ejemplo: ${config.objetivoCorrecciones} correcciones objetivo + auditorías propias + sueldo base`,
      admin: config.adminGanaSueldo 
        ? `Sueldo fijo: $${config.sueldoReferencia.toLocaleString()} + plus (sin francos)`
        : `Solo plus (sin sueldo base ni francos)`,
      contable: `Sueldo fijo: $${config.sueldoReferencia.toLocaleString()} + francos ($${config.valorDiaPago}/día) + plus`
    };
    
    return ejemplos[selectedUser.role] || '';
  };

  return (
    <div className="sueldos-admin-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1>💰 Configuración de Sueldos</h1>
            <p>Panel administrativo para configurar salarios y objetivos</p>
          </div>
          <button 
            className="back-to-dashboard-btn"
            onClick={() => window.location.href = '/dashboard'}
            title="Volver al Dashboard"
          >
            ← Volver al Dashboard
          </button>
        </div>
      </div>

      <div className="content-grid">
        {/* Lista de empleados */}
        <div className="empleados-list">
          <h3>👥 Empleados</h3>
          {loadingUsers ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{ 
                border: '2px solid #f3f4f6',
                borderTop: '2px solid #3b82f6',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 10px'
              }}></div>
              <p>Cargando empleados...</p>
            </div>
          ) : usuarios.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              <p>❌ No se encontraron empleados</p>
              <small>Verifica la conexión al servidor</small>
            </div>
          ) : (
            <div className="user-cards">
              {usuarios.map(user => (
              <div
                key={user.id}
                className={`user-card ${selectedUser?.id === user.id ? 'selected' : ''}`}
                onClick={() => handleUserSelect(user)}
              >
                <div className="user-info">
                  <span className="user-name">{user.name}</span>
                  <span className={`role-badge role-${user.role}`}>
                    {user.role.toUpperCase()}
                  </span>
                </div>
                <span className="user-email">{user.email}</span>
              </div>
              ))}
            </div>
          )}
        </div>

        {/* Configuración del empleado seleccionado */}
        {selectedUser && (
          <div className="config-panel">
            <h3>⚙️ Configurar: {selectedUser.name}</h3>
            
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                {/* Período */}
                <div className="form-group">
                  <label>📅 Período</label>
                  <div className="period-inputs">
                    <select
                      value={config.mes}
                      onChange={(e) => handlePeriodChange('mes', parseInt(e.target.value))}
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(2024, i).toLocaleDateString('es-AR', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      value={config.anio}
                      onChange={(e) => handlePeriodChange('anio', parseInt(e.target.value))}
                      min="2024"
                      max="2030"
                    />
                  </div>
                </div>

                {/* Sueldo referencia */}
                <div className="form-group">
                  <label>💵 Sueldo de Referencia</label>
                  <input
                    type="number"
                    value={config.sueldoReferencia}
                    onChange={(e) => {
                      const inputValue = e.target.value.replace(/[^\d.]/g, ''); // Limpiar caracteres no numéricos
                      const newSueldo = parseFloat(inputValue) || 0;
                      if (!isNaN(newSueldo) && newSueldo >= 0) {
                        setConfig(prev => ({ 
                          ...prev, 
                          sueldoReferencia: newSueldo,
                          valorDiaPago: Math.round(newSueldo / 30), // Auto-calcular día
                          valorHora: Math.round(newSueldo / 160) // Auto-calcular hora
                        }))
                      }
                    }}
                    min="0"
                    step="1000"
                  />
                  <small>
                    {config.tipoPago === 'horas'
                      ? 'Sueldo base de referencia (para calcular valor/hora)'
                      : selectedUser?.role === 'operador' 
                      ? 'Meta de dinero a ganar en auditorías' 
                      : config.esSueldoFijo ? 'Sueldo fijo mensual' : 'Meta a alcanzar'
                    }
                  </small>
                </div>

                {/* Tipo de pago - Solo para operadores */}
                {selectedUser.role === 'operador' && (
                  <div className="form-group">
                    <label>💼 Tipo de Pago</label>
                    <select
                      value={config.tipoPago}
                      onChange={(e) => {
                        const tipoPago = e.target.value as 'objetivos' | 'horas';
                        setConfig(prev => ({ 
                          ...prev, 
                          tipoPago: tipoPago,
                          valorHora: tipoPago === 'horas' ? Math.round(prev.sueldoReferencia / 160) : 0
                        }));
                      }}
                    >
                      <option value="objetivos">🎯 Por Objetivos (Auditorías)</option>
                      <option value="horas">⏰ Por Horas Trabajadas</option>
                    </select>
                    <small>
                      {config.tipoPago === 'horas' 
                        ? 'Operador nuevo - Se paga por tiempo trabajado'
                        : 'Operador experimentado - Se paga por auditorías completadas'
                      }
                    </small>
                  </div>
                )}

                {/* Valor por hora - Solo si es pago por horas */}
                {selectedUser.role === 'operador' && config.tipoPago === 'horas' && (
                  <div className="form-group">
                    <label>💰 Valor por Hora ($)</label>
                    <input
                      type="number"
                      value={config.valorHora || Math.round(config.sueldoReferencia / 160)}
                      onChange={(e) => setConfig(prev => ({ ...prev, valorHora: parseFloat(e.target.value) }))}
                      min="0"
                      step="50"
                      placeholder={Math.round(config.sueldoReferencia / 160).toString()}
                    />
                    <small>
                      Auto-calculado: ${Math.round(config.sueldoReferencia / 160)}/hora 
                      (${config.sueldoReferencia.toLocaleString()} ÷ 160h/mes)
                    </small>
                  </div>
                )}

                {/* Solo supervisor tiene objetivo de correcciones */}
                {selectedUser.role === 'supervisor' && (
                  <div className="form-group">
                    <label>✅ Objetivo Correcciones</label>
                    <input
                      type="number"
                      value={config.objetivoCorrecciones || 0}
                      onChange={(e) => setConfig(prev => ({ ...prev, objetivoCorrecciones: parseInt(e.target.value) }))}
                      min="0"
                    />
                    <small>Auditorías a revisar/corregir en el mes</small>
                  </div>
                )}

                {/* Configuración admin */}
                {selectedUser.role === 'admin' && (
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={config.adminGanaSueldo}
                        onChange={(e) => setConfig(prev => ({ ...prev, adminGanaSueldo: e.target.checked }))}
                      />
                      👑 Admin gana sueldo base
                    </label>
                    <small>Si no está marcado, solo cobra días pagos y plus</small>
                  </div>
                )}

                {/* Valor día pago - Solo para no-admin */}
                {selectedUser.role !== 'admin' && (
                  <div className="form-group">
                    <label>🗓️ Valor Día Pago (Automático)</label>
                    <input
                      type="number"
                      value={config.valorDiaPago}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
                    />
                    <small>Calculado automático: sueldo ÷ 30 días</small>
                  </div>
                )}

                {/* Plus - Tipo y valor */}
                <div className="form-group">
                  <label>➕ Tipo de Plus</label>
                  <select
                    value={config.plusTipo}
                    onChange={(e) => setConfig(prev => ({ 
                      ...prev, 
                      plusTipo: e.target.value as 'porcentaje' | 'fijo',
                      plusPorcentaje: e.target.value === 'fijo' ? 0 : prev.plusPorcentaje,
                      plusMontoFijo: e.target.value === 'porcentaje' ? 0 : prev.plusMontoFijo
                    }))}
                  >
                    <option value="porcentaje">📊 Porcentaje</option>
                    <option value="fijo">💰 Monto Fijo</option>
                  </select>
                  <small>Selecciona si el plus es por % o monto fijo</small>
                </div>

                {/* Campo dinámico según tipo de plus */}
                <div className="form-group">
                  <label>
                    {config.plusTipo === 'porcentaje' 
                      ? '📈 Plus Porcentaje (%)' 
                      : '💵 Plus Monto Fijo ($)'}
                  </label>
                  <input
                    type="number"
                    value={config.plusTipo === 'porcentaje' ? config.plusPorcentaje : config.plusMontoFijo}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (config.plusTipo === 'porcentaje') {
                        setConfig(prev => ({ ...prev, plusPorcentaje: value }));
                      } else {
                        setConfig(prev => ({ ...prev, plusMontoFijo: value }));
                      }
                    }}
                    min="0"
                    max={config.plusTipo === 'porcentaje' ? "100" : undefined}
                    step={config.plusTipo === 'porcentaje' ? "0.5" : "100"}
                  />
                  <small>
                    {config.plusTipo === 'porcentaje' 
                      ? 'Porcentaje adicional sobre el sueldo total' 
                      : 'Monto fijo adicional al sueldo'}
                  </small>
                </div>

                {/* Checkboxes - Solo para no-admin */}
                {selectedUser.role !== 'admin' && (
                  <>
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={config.cobraFrancos}
                          onChange={(e) => setConfig(prev => ({ ...prev, cobraFrancos: e.target.checked }))}
                        />
                        🏖️ Cobra días francos (domingos/feriados)
                      </label>
                      <small>Si no está marcado, solo cobra por trabajo</small>
                    </div>

                    {/* Calendario de Francos */}
                    {config.cobraFrancos && (
                      <div className="form-group franco-calendar">
                        <label>📅 Días Franco/Feriado - {new Date(config.anio, config.mes - 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}</label>
                        <div className="calendar-grid">
                          {(() => {
                            const year = config.anio;
                            const month = config.mes - 1; // JS months are 0-indexed
                            const firstDay = new Date(year, month, 1);
                            const lastDay = new Date(year, month + 1, 0);
                            const daysInMonth = lastDay.getDate();
                            const startingDayOfWeek = firstDay.getDay(); // 0 = domingo
                            
                            const days = [];
                            
                            // Días vacíos antes del primer día del mes
                            for (let i = 0; i < startingDayOfWeek; i++) {
                              days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
                            }
                            
                            // Días del mes
                            for (let day = 1; day <= daysInMonth; day++) {
                              const isMarked = config.francosMarcados?.includes(day) || false;
                              const dayOfWeek = new Date(year, month, day).getDay();
                              const isSunday = dayOfWeek === 0;
                              
                              days.push(
                                <div 
                                  key={day}
                                  className={`calendar-day ${isMarked ? 'marked' : ''} ${isSunday ? 'sunday' : ''}`}
                                  onClick={() => {
                                    const currentFrancos = config.francosMarcados || [];
                                    if (isMarked) {
                                      // Desmarcar
                                      setConfig(prev => ({
                                        ...prev,
                                        francosMarcados: currentFrancos.filter(d => d !== day)
                                      }));
                                    } else {
                                      // Marcar
                                      setConfig(prev => ({
                                        ...prev,
                                        francosMarcados: [...currentFrancos, day].sort((a, b) => a - b)
                                      }));
                                    }
                                  }}
                                >
                                  {day}
                                  {isMarked && <span className="franco-icon">🎉</span>}
                                </div>
                              );
                            }
                            
                            return days;
                          })()}
                        </div>
                        <small>
                          Clickeá para marcar días franco/feriado. 
                          Solo se pagan si el empleado trabajó antes y después.
                          Marcados: {config.francosMarcados?.length || 0} días
                        </small>
                      </div>
                    )}
                  </>
                )}

                {/* Fecha ingreso */}
                <div className="form-group">
                  <label>📅 Fecha Ingreso (opcional)</label>
                  <input
                    type="date"
                    value={config.fechaIngreso || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, fechaIngreso: e.target.value }))}
                  />
                  <small>Para cálculo proporcional si ingresó mid-month</small>
                </div>

                {/* Días de vacaciones */}
                <div className="form-group">
                  <label>🏝️ Días Vacaciones</label>
                  <input
                    type="number"
                    value={config.diasVacaciones}
                    onChange={(e) => setConfig(prev => ({ ...prev, diasVacaciones: parseInt(e.target.value) }))}
                    min="0"
                    max="31"
                  />
                  <small>Días de vacaciones tomadas este mes</small>
                </div>
              </div>

              {/* Ejemplo de cálculo */}
              <div className="calculation-example">
                <h4>📊 Ejemplo de Cálculo:</h4>
                <p>{calcularMesExamples()}</p>
              </div>

              {/* Botones */}
              <div className="form-actions">
                <button 
                  type="submit" 
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? '⏳ Guardando...' : '💾 Configurar Sueldo'}
                </button>
              </div>

              {message && (
                <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}
            </form>
          </div>
        )}
      </div>

      <style jsx>{`
        .sueldos-admin-page {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .page-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .page-header h1 {
          color: #1f2937;
          margin-bottom: 8px;
        }

        .back-to-dashboard-btn {
          position: absolute;
          top: 20px;
          left: 20px;
          background: #3b82f6;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
          z-index: 10;
        }

        .back-to-dashboard-btn:hover {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .content-grid {
          display: grid;
          grid-template-columns: 350px 1fr;
          gap: 30px;
          min-height: 600px;
        }

        .empleados-list {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          height: fit-content;
        }

        .empleados-list h3 {
          margin-bottom: 16px;
          color: #1f2937;
        }

        .user-cards {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .user-card {
          padding: 12px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .user-card:hover {
          border-color: #3b82f6;
          background-color: #f8fafc;
        }

        .user-card.selected {
          border-color: #3b82f6;
          background-color: #eff6ff;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }

        .user-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }

        .user-name {
          font-weight: 600;
          color: #1f2937;
        }

        .role-badge {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .role-operador { background: #dbeafe; color: #1e40af; }
        .role-supervisor { background: #dcfce7; color: #166534; }
        .role-admin { background: #fef3c7; color: #92400e; }
        .role-contable { background: #e0e7ff; color: #3730a3; }

        .user-email {
          font-size: 12px;
          color: #6b7280;
        }

        .config-panel {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .config-panel h3 {
          margin-bottom: 20px;
          color: #1f2937;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-weight: 500;
          color: #374151;
          font-size: 14px;
        }

        .form-group input,
        .form-group select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }

        .form-group small {
          color: #6b7280;
          font-size: 12px;
        }

        .period-inputs {
          display: flex;
          gap: 8px;
        }

        .period-inputs select,
        .period-inputs input {
          flex: 1;
        }

        .checkbox-label {
          display: flex !important;
          flex-direction: row !important;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .checkbox-label input[type="checkbox"] {
          width: 16px;
          height: 16px;
          margin: 0;
        }

        .calculation-example {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .calculation-example h4 {
          margin-bottom: 8px;
          color: #1f2937;
        }

        .calculation-example p {
          color: #4b5563;
          margin: 0;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 13px;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .btn-primary {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          background: #2563eb;
        }

        .btn-primary:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .message {
          padding: 12px;
          border-radius: 6px;
          margin-top: 12px;
          text-align: center;
          font-weight: 500;
        }

        .message.success {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .message.error {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        /* Calendario de Francos */
        .franco-calendar {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          background: #f9fafb;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
          margin: 12px 0;
          max-width: 350px;
        }

        .calendar-day {
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          background: white;
          position: relative;
          transition: all 0.2s;
          min-height: 32px;
        }

        .calendar-day:hover:not(.empty) {
          border-color: #3b82f6;
          background: #f0f9ff;
        }

        .calendar-day.empty {
          cursor: default;
          background: transparent;
          border: none;
        }

        .calendar-day.sunday {
          background: #fef3c7;
          border-color: #fbbf24;
        }

        .calendar-day.marked {
          background: #dcfce7 !important;
          border-color: #10b981 !important;
          color: #065f46;
          font-weight: 600;
        }

        .franco-icon {
          position: absolute;
          top: -2px;
          right: -2px;
          font-size: 8px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .content-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default SueldosAdminPage;